import { Markdown } from '@plataforma/markdown';
import { useState } from 'react';
import { useConsulta } from './api';
import { Asistente } from './asistente';

export interface ResumenArticulo {
  id: string;
  slug: string;
  coleccion: 'manual' | 'ayuda';
  capitulo: string;
  orden: number;
  titulo: string;
  resumen: string;
}

/**
 * Ayuda en los paneles (arquitectura 2.12): Manual por capítulos, preguntas frecuentes y búsqueda
 * en la base de conocimiento. Lo que no se resuelve aquí va a un ticket (`enlaceTicket`).
 */
export function Ayuda({ enlaceTicket }: { enlaceTicket?: string }) {
  const [q, setQ] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [slug, setSlug] = useState<string | null>(null);
  const indice = useConsulta<ResumenArticulo[]>('/platform/conocimiento');
  const resultados = useConsulta<ResumenArticulo[]>(
    busqueda ? `/platform/conocimiento/buscar?q=${encodeURIComponent(busqueda)}` : null,
  );
  const articulo = useConsulta<ResumenArticulo & { contenido: string }>(
    slug ? `/platform/conocimiento/${slug}` : null,
  );
  const grupos = new Map<string, ResumenArticulo[]>();
  for (const a of indice.datos ?? []) {
    const clave = `${a.coleccion === 'manual' ? 'Manual' : 'Preguntas frecuentes'} · ${a.capitulo}`;
    grupos.set(clave, [...(grupos.get(clave) ?? []), a]);
  }
  if (slug)
    return (
      <>
        <div className="encabezado">
          <div>
            <button className="boton" onClick={() => setSlug(null)}>
              ← Ayuda
            </button>
            <h1>{articulo.datos?.titulo ?? '…'}</h1>
            <div className="subtitulo">{articulo.datos?.resumen}</div>
          </div>
        </div>
        {articulo.error && <p className="error">{articulo.error}</p>}
        {articulo.datos && (
          <section className="seccion cuerpo">
            <Markdown texto={articulo.datos.contenido} />
          </section>
        )}
        {enlaceTicket && (
          <p className="subtitulo">
            ¿No se resolvió? <a href={enlaceTicket}>Abre un ticket con Soporte</a>.
          </p>
        )}
      </>
    );
  const lista = (items: ResumenArticulo[]) => (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {items.map((a) => (
        <li key={a.id} style={{ margin: '6px 0' }}>
          <button className="enlace" onClick={() => setSlug(a.slug)}>
            {a.titulo}
          </button>
          <div className="subtitulo">{a.resumen}</div>
        </li>
      ))}
    </ul>
  );
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Ayuda</h1>
          <div className="subtitulo">Manual, preguntas frecuentes y soporte</div>
        </div>
        <div className="relleno" />
        {enlaceTicket && (
          <a className="boton primario" href={enlaceTicket}>
            Hablar con Soporte
          </a>
        )}
      </div>
      <Asistente alAbrirArticulo={setSlug} />
      <form
        className="seccion cuerpo"
        onSubmit={(e) => {
          e.preventDefault();
          setBusqueda(q.trim());
        }}
        style={{ display: 'flex', gap: 8 }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="¿Qué necesitas? Por ejemplo: dominio propio, VIN, contraseña"
          style={{ flex: 1 }}
        />
        <button className="boton">Buscar</button>
      </form>
      {busqueda && (
        <section className="seccion">
          <header>
            <h2>Resultados para «{busqueda}»</h2>
          </header>
          <div className="cuerpo">
            {resultados.datos?.length ? (
              lista(resultados.datos)
            ) : (
              <p className="vacio">Sin resultados.</p>
            )}
          </div>
        </section>
      )}
      {[...grupos.entries()].map(([titulo, items]) => (
        <section className="seccion" key={titulo}>
          <header>
            <h2>{titulo}</h2>
          </header>
          <div className="cuerpo">{lista(items)}</div>
        </section>
      ))}
    </>
  );
}
